import { Layout } from '@/components/layout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Shield, Award, ArrowRight } from 'lucide-react';
import { productsData } from '@/data/productsData';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

const WheyProteinPage = () => {
  const wheyProducts = productsData.filter(p => p.category === 'whey');

  return (
    <>
      <Helmet>
        <title>Buy Whey Protein in India & Nepal – 100% Authentic | NutraFuel Supplements®</title>
        <meta name="description" content="Shop premium whey protein in India & Nepal. 100% authentic products from top brands. Fast delivery, lab-verified quality. Trusted by athletes across India & Nepal." />
        <meta name="keywords" content="buy whey protein india, whey protein nepal, authentic whey protein, whey isolate india, protein supplements india nepal" />
        <link rel="canonical" href="https://nutrafuel.in/whey-protein" />
        <meta property="og:title" content="Buy Whey Protein in India & Nepal – 100% Authentic" />
        <meta property="og:description" content="Shop premium whey protein in India & Nepal. 100% authentic, lab-verified quality." />
        <meta property="og:url" content="https://nutrafuel.in/whey-protein" />
      </Helmet>

      <Layout>
        {/* Breadcrumb */}
        <div className="bg-muted/30 py-3 border-b border-border">
          <div className="container mx-auto px-4">
            <nav className="text-sm text-muted-foreground">
              <Link to="/" className="hover:text-primary transition-colors">Home</Link>
              <span className="mx-2">/</span>
              <span className="text-foreground font-medium">Whey Protein</span>
            </nav>
          </div>
        </div>

        {/* Hero Section */}
        <section className="relative pt-32 md:pt-40 pb-16 bg-gradient-to-b from-muted/50 to-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <Badge className="mb-4 bg-gold text-foreground">Premium Category</Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-foreground leading-tight mb-6">
                Buy Whey Protein in India & Nepal – 100% Authentic
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                Shop premium whey protein supplements in India and Nepal at NutraFuel Supplements. Trusted by athletes for muscle growth, recovery, and performance. Every product is 100% authentic and lab-verified.
              </p>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 text-green-600">
                  <Shield className="w-4 h-4" />
                  <span className="text-sm font-medium">100% Authentic</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 text-green-600">
                  <Award className="w-4 h-4" />
                  <span className="text-sm font-medium">Lab Verified</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">Fast Delivery</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Products Grid */}
        <section className="py-12 bg-background">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-display font-bold text-foreground mb-8">
              Premium Whey Protein Products
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-16">
              {wheyProducts.map((product) => (
                <Link key={product.id} to={`/products/${product.slug}`}>
                  <Card className="group overflow-hidden border-0 shadow-soft hover:shadow-elevated transition-all duration-300 hover:-translate-y-2 flex flex-col h-full">
                    <div className="relative aspect-square overflow-hidden bg-muted">
                      <img
                        src={product.image}
                        alt={`${product.name} - Premium whey protein supplement`}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                      />
                      {product.popular && (
                        <Badge className="absolute top-3 left-3 bg-gold text-foreground">
                          Popular
                        </Badge>
                      )}
                      <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-success/90 flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-success-foreground" />
                      </div>
                    </div>
                    <CardContent className="p-5 flex flex-col flex-1">
                      <span className="text-xs text-gold font-medium uppercase tracking-wider mb-1">{product.brand}</span>
                      <h3 className="font-bold text-foreground mb-2">{product.name}</h3>
                      <p className="text-sm text-muted-foreground mb-4 flex-1">{product.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {product.features.map((feature) => (
                          <span key={feature} className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">
                            {feature}
                          </span>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>

            {/* SEO Content Section */}
            <div className="max-w-4xl mx-auto prose prose-lg">
              <article className="bg-white rounded-lg p-8 shadow-sm">
                <h2 className="text-2xl font-display font-bold text-foreground mb-6">
                  Why Choose Whey Protein from NutraFuel Supplements?
                </h2>
                
                <p className="text-muted-foreground leading-relaxed mb-6">
                  NutraFuel Supplements is India and Nepal&apos;s most trusted destination for authentic whey protein supplements. Whether you&apos;re a beginner starting your fitness journey or a professional athlete, our carefully curated selection of whey protein products ensures you get only the best quality supplements for your muscle building and recovery goals.
                </p>

                <h3 className="text-xl font-bold text-foreground mb-4">
                  What Makes Our Whey Protein Special?
                </h3>

                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-1" />
                    <div>
                      <strong>100% Authentic & Lab-Verified:</strong> Every whey protein product is sourced directly from authorized distributors and undergoes strict quality checks. We guarantee zero fake or counterfeit products.
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-1" />
                    <div>
                      <strong>Top Indian & International Brands:</strong> We stock premium brands like Optimum Nutrition, MuscleBlaze, MyProtein, and more – all trusted by millions of athletes worldwide.
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-1" />
                    <div>
                      <strong>Fast Delivery Across India & Nepal:</strong> Order today and receive your whey protein supplements within 2-5 business days across major cities in India and Nepal.
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-1" />
                    <div>
                      <strong>Expert Guidance:</strong> Our team of fitness experts helps you choose the right whey protein based on your goals, budget, and dietary requirements.
                    </div>
                  </li>
                </ul>

                <h2 className="text-2xl font-display font-bold text-foreground mb-6">
                  Best Whey Protein for Muscle Gain in India & Nepal
                </h2>

                <p className="text-muted-foreground leading-relaxed mb-6">
                  Whey protein is the gold standard for muscle building and post-workout recovery. Our whey protein range includes isolates, concentrates, and blends designed to support lean muscle growth, faster recovery, and improved strength. Whether you&apos;re bulking, cutting, or maintaining, we have the perfect whey protein for your fitness goals.
                </p>

                <h3 className="text-xl font-bold text-foreground mb-4">
                  Types of Whey Protein We Offer:
                </h3>

                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-muted/50 rounded-lg p-6">
                    <h4 className="font-bold text-foreground mb-2">Whey Protein Isolate</h4>
                    <p className="text-sm text-muted-foreground">
                      90%+ protein content, minimal carbs and fats. Best for cutting phases and lactose-intolerant individuals. Fast absorption for quick muscle recovery.
                    </p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-6">
                    <h4 className="font-bold text-foreground mb-2">Whey Protein Concentrate</h4>
                    <p className="text-sm text-muted-foreground">
                      70-80% protein, affordable, great taste. Ideal for beginners and daily protein supplementation. Excellent value for money.
                    </p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-6">
                    <h4 className="font-bold text-foreground mb-2">Whey Protein Blend</h4>
                    <p className="text-sm text-muted-foreground">
                      Combination of isolate and concentrate. Balanced protein delivery for sustained muscle growth throughout the day.
                    </p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-6">
                    <h4 className="font-bold text-foreground mb-2">Mass Gainers</h4>
                    <p className="text-sm text-muted-foreground">
                      High-calorie whey protein with added carbs. Perfect for hardgainers and those looking to add serious muscle mass.
                    </p>
                  </div>
                </div>

                <h2 className="text-2xl font-display font-bold text-foreground mb-6">
                  Benefits of Whey Protein Supplements
                </h2>

                <p className="text-muted-foreground leading-relaxed mb-4">
                  Whey protein is scientifically proven to support muscle protein synthesis, the process your body uses to build new muscle tissue. Here&apos;s why whey protein is essential for anyone serious about fitness in India and Nepal:
                </p>

                <ul className="space-y-2 mb-8">
                  <li className="flex items-center gap-2">
                    <ArrowRight className="w-4 h-4 text-primary" />
                    <span>Accelerates muscle growth and recovery after intense workouts</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <ArrowRight className="w-4 h-4 text-primary" />
                    <span>Provides complete amino acid profile including all 9 essential amino acids</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <ArrowRight className="w-4 h-4 text-primary" />
                    <span>Fast-absorbing protein reaches muscles quickly post-workout</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <ArrowRight className="w-4 h-4 text-primary" />
                    <span>Supports fat loss while preserving lean muscle mass</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <ArrowRight className="w-4 h-4 text-primary" />
                    <span>Convenient way to meet daily protein requirements</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <ArrowRight className="w-4 h-4 text-primary" />
                    <span>Boosts immunity and overall health with bioactive compounds</span>
                  </li>
                </ul>

                <h2 className="text-2xl font-display font-bold text-foreground mb-6">
                  How to Choose the Right Whey Protein
                </h2>

                <p className="text-muted-foreground leading-relaxed mb-6">
                  Selecting the best whey protein for your needs depends on several factors. At NutraFuel Supplements, we help customers across India and Nepal make informed decisions based on their fitness goals, budget, and dietary preferences.
                </p>

                <div className="bg-primary/5 border-l-4 border-primary rounded p-6 mb-8">
                  <h4 className="font-bold text-foreground mb-3">Expert Recommendation:</h4>
                  <p className="text-muted-foreground">
                    For muscle building, we recommend whey protein isolate or a quality blend with at least 24g protein per serving. Beginners can start with whey concentrate for better value, while advanced athletes benefit from fast-absorbing isolates. Combine with <Link to="/creatine" className="text-primary hover:underline">creatine monohydrate</Link> for maximum muscle gains.
                  </p>
                </div>

                {/* FAQ Section */}
                <h2 className="text-2xl font-display font-bold text-foreground mb-6">
                  Frequently Asked Questions About Whey Protein
                </h2>

                <div className="space-y-6">
                  <div className="border-b border-border pb-6">
                    <h3 className="text-lg font-semibold text-foreground mb-3">
                      Which whey protein is best for beginners in India?
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      For beginners in India and Nepal, whey protein concentrate or blend is ideal. It provides excellent protein quality (70-80% protein content) at an affordable price point. Brands like MuscleBlaze Biozyme and Optimum Nutrition Gold Standard are perfect starter options. These are easier to digest and offer great value for money while you build your fitness foundation.
                    </p>
                  </div>

                  <div className="border-b border-border pb-6">
                    <h3 className="text-lg font-semibold text-foreground mb-3">
                      How much whey protein do I need per day?
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      For muscle building, aim for 1.6-2.2g of protein per kg of body weight daily. If you weigh 70kg, that&apos;s 112-154g of protein per day. One scoop of whey protein (typically 24-25g) post-workout, combined with protein from whole foods, helps you easily meet your daily protein goals without excessive calories.
                    </p>
                  </div>

                  <div className="border-b border-border pb-6">
                    <h3 className="text-lg font-semibold text-foreground mb-3">
                      When should I take whey protein for best results?
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      The optimal time to take whey protein is within 30-60 minutes post-workout when your muscles are most receptive to nutrients. However, you can also take it as a breakfast protein boost, between meals to curb hunger, or before bed (casein blend preferred). Consistency matters more than exact timing.
                    </p>
                  </div>

                  <div className="border-b border-border pb-6">
                    <h3 className="text-lg font-semibold text-foreground mb-3">
                      Is whey protein safe for daily use in India & Nepal?
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      Yes, whey protein is completely safe for daily consumption when sourced from authentic brands. At NutraFuel Supplements, all our whey protein products are 100% genuine, manufactured under strict quality controls, and suitable for the Indian and Nepalese climate. Stay hydrated and follow recommended serving sizes.
                    </p>
                  </div>

                  <div className="pb-6">
                    <h3 className="text-lg font-semibold text-foreground mb-3">
                      How do I know if whey protein is authentic?
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      Authentic whey protein has proper labeling, batch codes, and authorization marks. At NutraFuel, we source directly from authorized distributors, provide lab verification certificates, and guarantee 100% authenticity. Beware of products priced too low – they&apos;re often fake or expired. Read our guide on <Link to="/blog/identify-fake-supplements" className="text-primary hover:underline">identifying fake supplements in India</Link>.
                    </p>
                  </div>
                </div>

                {/* Related Categories */}
                <div className="mt-12 pt-8 border-t border-border">
                  <h2 className="text-2xl font-display font-bold text-foreground mb-6">
                    Related Categories
                  </h2>
                  <div className="grid md:grid-cols-3 gap-4">
                    <Link to="/creatine" className="block p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
                      <h4 className="font-bold text-foreground mb-2">Creatine Monohydrate</h4>
                      <p className="text-sm text-muted-foreground">Boost strength and power output</p>
                    </Link>
                    <Link to="/pre-workout" className="block p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
                      <h4 className="font-bold text-foreground mb-2">Pre-Workout Supplements</h4>
                      <p className="text-sm text-muted-foreground">Explosive energy for intense training</p>
                    </Link>
                    <Link to="/products" className="block p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
                      <h4 className="font-bold text-foreground mb-2">All Products</h4>
                      <p className="text-sm text-muted-foreground">Browse complete supplement range</p>
                    </Link>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-primary/10 via-gold/10 to-primary/10">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-display font-bold text-foreground mb-4">
              Need Help Choosing the Right Whey Protein?
            </h2>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              Our nutrition experts are available on WhatsApp to help you select the perfect whey protein supplement based on your fitness goals and budget.
            </p>
            <Button variant="default" size="lg" className="bg-green-600 hover:bg-green-700" asChild>
              <a href="https://wa.me/9779824042067?text=Hi!%20I%20need%20help%20choosing%20whey%20protein" target="_blank" rel="noopener noreferrer">
                <MessageCircle className="w-5 h-5 mr-2" />
                Get Expert Advice on WhatsApp
              </a>
            </Button>
          </div>
        </section>
      </Layout>
    </>
  );
};

export default WheyProteinPage;
